'use client';

import * as React from 'react';
import { getRequestsByCommunityId } from '@/api/requests';
import { getAnnouncements } from '@/api/announcements';
import { getResidents } from '@/api/residents';
import { getAllUnits } from '@/api/units';
import axiosClient from '@/utils/client';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import {
  Buildings as BuildingsIcon,
  ClipboardText as RequestIcon,
  Folder as FolderIcon,
  MagnifyingGlass as SearchIcon,
  Megaphone as AnnouncementIcon,
  User as UserIcon,
  X as XIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';

import type { Request } from '@/types/v2/request';
import type { Announcement } from '@/types/announcement';
import type { Unit } from '@/types/v2/unit';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { hasCommunityRolesV2 } from '@/utils/has-roles';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ResultKind = 'request' | 'announcement' | 'unit' | 'document' | 'resident';

interface SearchResult {
  id: string;
  kind: ResultKind;
  title: string;
  subtitle?: string;
  badge?: string;
  badgeColor?: string;
  href: string;
}

// ---------------------------------------------------------------------------
// Icon per kind
// ---------------------------------------------------------------------------

function KindIcon({ kind }: { kind: ResultKind }) {
  const props = { size: 18 };
  const colors: Record<ResultKind, string> = {
    request: 'var(--mui-palette-primary-main)',
    announcement: 'var(--mui-palette-warning-main)',
    unit: 'var(--mui-palette-success-main)',
    document: 'var(--mui-palette-info-main)',
    resident: 'var(--mui-palette-secondary-main)',
  };
  const icons: Record<ResultKind, React.ReactNode> = {
    request: <RequestIcon {...props} />,
    announcement: <AnnouncementIcon {...props} />,
    unit: <BuildingsIcon {...props} />,
    document: <FolderIcon {...props} />,
    resident: <UserIcon {...props} />,
  };
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${colors[kind]}18`,
        color: colors[kind],
        flexShrink: 0,
      }}
    >
      {icons[kind]}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Single result row
// ---------------------------------------------------------------------------

interface ResultRowProps {
  result: SearchResult;
  onClose: () => void;
  isHighlighted: boolean;
  rowRef?: React.Ref<HTMLDivElement>;
}

function ResultRow({ result, onClose, isHighlighted, rowRef }: ResultRowProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(result.href);
    onClose();
  };

  return (
    <Stack
      direction="row"
      onClick={handleClick}
      ref={rowRef}
      spacing={1.5}
      sx={{
        alignItems: 'center',
        cursor: 'pointer',
        px: 2,
        py: 1.25,
        borderRadius: 1,
        mx: 1,
        bgcolor: isHighlighted ? 'action.selected' : 'transparent',
        transition: 'background-color 100ms',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <KindIcon kind={result.kind} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {result.title}
          </Typography>
          {result.badge && (
            <Chip
              label={result.badge}
              size="small"
              sx={{
                fontSize: '0.65rem',
                height: 18,
                bgcolor: result.badgeColor ? `${result.badgeColor}22` : undefined,
                color: result.badgeColor ?? undefined,
                border: result.badgeColor ? `1px solid ${result.badgeColor}44` : undefined,
                fontWeight: 600,
              }}
            />
          )}
        </Stack>
        {result.subtitle && (
          <Typography color="text.secondary" noWrap sx={{ fontSize: '0.75rem', mt: 0.1 }}>
            {result.subtitle}
          </Typography>
        )}
      </Box>

      <Typography
        color="text.disabled"
        sx={{
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        {result.kind}
      </Typography>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function SectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', px: 3, pt: 1.5, pb: 0.5 }}
    >
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.disabled',
        }}
      >
        {label}
      </Typography>
      <Chip label={count} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

export interface SearchDialogProps {
  onClose?: () => void;
  open?: boolean;
}

export function SearchDialog({ onClose, open = false }: SearchDialogProps): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const [highlighted, setHighlighted] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { community } = useUser();

  // Determine user's access level
  const isManager = community && hasCommunityRolesV2(community, [COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.BOARD_MEMBER]);

  // Focus input on open
  React.useEffect(() => {
    if (open) {
      setQuery('');
      setHighlighted(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ── Data queries (stale-while-revalidate from existing cache) ──────────
  const { data: requestsData } = useQuery<{ requests: Request[] }>({
    queryKey: ['requests', community?.id],
    queryFn: () =>
      getRequestsByCommunityId(community?.id, {
        params: { sort: '-created_at', include: 'requestType,requestStatus,unit' },
      }),
    enabled: !!community?.id,
    staleTime: 60_000,
  });

  const { data: announcements = [], isFetching: fetchingAnnouncements } = useQuery<Announcement[]>({
    queryKey: ['announcements', community?.id],
    queryFn: () => getAnnouncements(community?.id?.toString()),
    enabled: !!community?.id,
    staleTime: 60_000,
  });

  const { data: unitsData, isFetching: fetchingUnits } = useQuery<{ units: Unit[] }>({
    queryKey: ['units', 'all'],
    queryFn: () => getAllUnits(),
    enabled: isManager && !!community?.id,
    staleTime: 60_000,
  });

  const { data: foldersData, isFetching: fetchingFolders } = useQuery({
    queryKey: ['folders'],
    queryFn: async () => {
      const res = await axiosClient.get('/api/v1/folders');
      return res.data;
    },
    enabled: isManager && !!community?.id,
    staleTime: 60_000,
  });

  const { data: residentsData, isFetching: fetchingResidents } = useQuery<{ residents: Array<{ id: string; uuid?: string; first_name?: string; last_name?: string; email?: string; name?: string }> }>({
    queryKey: ['residents', community?.id],
    queryFn: () => getResidents(community?.id, { all: true }),
    enabled: isManager && !!community?.id,
    staleTime: 60_000,
  });

  const isLoading = fetchingAnnouncements || fetchingUnits || fetchingFolders || fetchingResidents;

  // ── Build results ───────────────────────────────────────────────────────
  const results = React.useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const out: SearchResult[] = [];

    // Requests (all users see their requests and community-wide requests)
    for (const r of requestsData?.requests ?? []) {
      if (
        r.summary?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q) ||
        r.display_id?.toLowerCase().includes(q) ||
        r.request_type?.name?.toLowerCase().includes(q) ||
        r.request_status?.name?.toLowerCase().includes(q)
      ) {
        const unitLabel = r.unit
          ? `${r.unit.address}${r.unit.unit_number ? ` · #${r.unit.unit_number}` : ''}`
          : undefined;
        out.push({
          id: `req-${r.id}`,
          kind: 'request',
          title: r.summary,
          subtitle: [r.request_type?.name ? toTitleCase(r.request_type.name) : null, unitLabel]
            .filter(Boolean)
            .join(' · '),
          badge: r.request_status?.name ? toTitleCase(r.request_status.name) : undefined,
          badgeColor: r.request_status?.color,
          href: paths.requests.request(r.uuid ?? r.id.toString()),
        });
      }
    }

    // Announcements (all users see announcements)
    for (const a of announcements) {
      if (
        a.subject?.toLowerCase().includes(q) ||
        a.message?.toLowerCase().includes(q)
      ) {
        out.push({
          id: `ann-${a.id}`,
          kind: 'announcement',
          title: a.subject,
          subtitle: a.message?.slice(0, 80),
          href: paths.announcements.announcement(a.uuid ?? String(a.id)),
        });
      }
    }

    // Units (managers and board members only)
    if (isManager) {
      for (const u of unitsData?.units ?? []) {
        if (
          u.address?.toLowerCase().includes(q) ||
          u.unit_number?.toLowerCase().includes(q) ||
          u.city?.toLowerCase().includes(q)
        ) {
          out.push({
            id: `unit-${u.id}`,
            kind: 'unit',
            title: u.address ?? `Unit ${u.unit_number}`,
            subtitle: [u.unit_number ? `#${u.unit_number}` : null, u.city, u.state]
              .filter(Boolean)
              .join(' · '),
            href: paths.units.details(u.id),
          });
        }
      }
    }

    // Residents (managers and board members only)
    if (isManager) {
      for (const user of residentsData?.residents ?? []) {
        const firstName = user.first_name ?? '';
        const lastName = user.last_name ?? '';
        const fullName = ([firstName, lastName].filter(Boolean).join(' ').trim() || user.name) ?? '';
        const email = user.email ?? '';
        if (
          (firstName.toLowerCase().includes(q) ||
            lastName.toLowerCase().includes(q) ||
            fullName.toLowerCase().includes(q) ||
            email.toLowerCase().includes(q)) &&
          user.uuid
        ) {
          out.push({
            id: `resident-${user.id}`,
            kind: 'resident',
            title: fullName || email || `User ${user.id}`,
            subtitle: email || undefined,
            href: paths.users.details(user.uuid),
          });
        }
      }
    }

    // Documents / Folders (managers and board members only)
    if (isManager) {
      for (const f of foldersData?.folders ?? []) {
        if (f.name?.toLowerCase().includes(q)) {
          out.push({
            id: `folder-${f.uuid ?? f.id}`,
            kind: 'document',
            title: f.name,
            subtitle: f.item_count ? `${f.item_count} item${f.item_count !== 1 ? 's' : ''}` : undefined,
            href: paths.documents.folder(f.uuid ?? f.id),
          });
        }
      }
      for (const f of foldersData?.files ?? []) {
        if ((f.name_original ?? f.name)?.toLowerCase().includes(q)) {
          out.push({
            id: `file-${f.id}`,
            kind: 'document',
            title: f.name_original ?? f.name,
            href: paths.documents.index,
          });
        }
      }
    }

    return out;
  }, [query, requestsData, announcements, unitsData, residentsData, foldersData, isManager]);

  // Group by kind
  const groups = React.useMemo(() => {
    const map: Partial<Record<ResultKind, SearchResult[]>> = {};
    for (const r of results) {
      if (!map[r.kind]) map[r.kind] = [];
      map[r.kind]!.push(r);
    }
    return map;
  }, [results]);

  const groupOrder: ResultKind[] = isManager 
    ? ['request', 'announcement', 'unit', 'resident', 'document']
    : ['request', 'announcement'];
    
  const groupLabels: Record<ResultKind, string> = {
    request: 'Requests',
    announcement: 'Announcements',
    unit: 'Units',
    resident: 'Residents',
    document: 'Documents',
  };

  // Flat ordered list for keyboard nav
  const flatResults = groupOrder.flatMap((k) => groups[k] ?? []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && flatResults[highlighted]) {
      onClose?.();
    } else if (e.key === 'Escape') {
      onClose?.();
    }
  };

  const handleClose = () => {
    setQuery('');
    onClose?.();
  };

  const searchPlaceholder = isManager
    ? 'Search requests, announcements, units, residents, documents…'
    : 'Search requests and announcements…';

  const emptyMessage = isManager
    ? 'Search across requests, announcements, units, residents, and documents'
    : 'Search across requests and announcements';

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'var(--mui-shadows-24)',
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Search input ─────────────────────────────── */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <OutlinedInput
          autoFocus
          fullWidth
          inputRef={inputRef}
          endAdornment={
            query ? (
              <InputAdornment position="end">
                <IconButton onClick={() => setQuery('')} size="small" edge="end">
                  <XIcon size={16} />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlighted(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder={searchPlaceholder}
          size="small"
          startAdornment={
            <InputAdornment position="start">
              {isLoading && query ? (
                <CircularProgress size={16} />
              ) : (
                <SearchIcon size={18} style={{ color: 'var(--mui-palette-text-secondary)' }} />
              )}
            </InputAdornment>
          }
          sx={{
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
          }}
          value={query}
        />
      </Box>

      {/* ── Results ──────────────────────────────────── */}
      <DialogContent
        sx={{ p: 0, pb: 1.5, maxHeight: 480, overflowY: 'auto' }}
      >
        {!query ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <SearchIcon
              size={32}
              style={{ color: 'var(--mui-palette-text-disabled)', marginBottom: 8 }}
            />
            <Typography color="text.secondary" variant="body2">
              {emptyMessage}
            </Typography>
            <Typography color="text.disabled" sx={{ mt: 0.5 }} variant="caption">
              Use <kbd style={{ fontFamily: 'monospace', padding: '1px 5px', borderRadius: 3, border: '1px solid', borderColor: 'inherit' }}>↑</kbd>{' '}
              <kbd style={{ fontFamily: 'monospace', padding: '1px 5px', borderRadius: 3, border: '1px solid', borderColor: 'inherit' }}>↓</kbd>{' '}
              to navigate, <kbd style={{ fontFamily: 'monospace', padding: '1px 5px', borderRadius: 3, border: '1px solid', borderColor: 'inherit' }}>↵</kbd>{' '}
              to open
            </Typography>
          </Box>
        ) : flatResults.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              No results for &ldquo;{query}&rdquo;
            </Typography>
          </Box>
        ) : (
          <>
            {groupOrder.map((kind) => {
              const items = groups[kind];
              if (!items?.length) return null;

              return (
                <Box key={kind}>
                  <SectionHeader label={groupLabels[kind]} count={items.length} />
                  {items.map((result) => {
                    const globalIdx = flatResults.indexOf(result);
                    return (
                      <ResultRow
                        isHighlighted={highlighted === globalIdx}
                        key={result.id}
                        onClose={handleClose}
                        result={result}
                      />
                    );
                  })}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>

      {/* ── Footer hint ──────────────────────────────── */}
      {flatResults.length > 0 && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 1 }}>
          <Typography color="text.disabled" variant="caption">
            {flatResults.length} result{flatResults.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}
